import { useState, useEffect } from "react";
import { AcademicYear, AcademicYearInitializationResult, AcademicYearStatus, AcademicYearSyncResult, EnrollmentInitializationResult } from "../types";
import {
  closeAcademicYear,
  fetchAcademicYears,
  initializeAcademicYear,
  createAcademicYear,
  createPreviousAcademicYear,
  deleteAcademicYear,
  activateAcademicYear,
  syncActiveAcademicYear,
} from "../services/academicYearsService";

export const useAcademicYears = () => {
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [creating, setCreating] = useState(false);
  const [creatingPrevious, setCreatingPrevious] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initializationResult, setInitializationResult] = useState<EnrollmentInitializationResult | null>(null);
  const [academicYearSyncResult, setAcademicYearSyncResult] = useState<AcademicYearSyncResult | null>(null);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchAcademicYears();
        setAcademicYears(data);

      } catch (err) {
        setError("Failed to load academic years.");
      } finally {
        setLoading(false);
        setLoaded(true);
      }
    };
    load();
  }, []);

  const create = async () => {
    setCreating(true);
    setError(null);
    try {
      const newAcademicYear = await createAcademicYear();
      setAcademicYears((prev) => [...prev, newAcademicYear]);
    } catch (err) {
      setError("Failed to create academic year.");
    } finally {
      setCreating(false);
    }
  };

  // Throws on failure so the caller can show the backend's message
  const createPrevious = async (): Promise<AcademicYear> => {
    setCreatingPrevious(true);
    try {
      const previousAcademicYear = await createPreviousAcademicYear();
      // List is newest first, so the new oldest year goes at the end
      setAcademicYears((prev) => [...prev, previousAcademicYear]);
      return previousAcademicYear;
    } finally {
      setCreatingPrevious(false);
    }
  };

  const initialize = async (name: string) => {
    try {
      const aYInitResult: AcademicYearInitializationResult = await initializeAcademicYear(name);
      setAcademicYears((prev) => prev.map((ay) => (ay.name === name ? aYInitResult.academicYearDto : ay)));
    } catch (err) {
      setError("Failed to initialize academic year.");
    }
  };

  const activate = async (name: string) => {
    try {
      const updated: AcademicYear = await activateAcademicYear(name);
      setAcademicYears((prev) => prev.map((ay) => (ay.name === name ? updated : ay)));
    } catch (err) {
      setError("Failed to initialize academic year.");
    }
  };

  const sync = async () => {
    try {
      setLoading(true);
      const data = await syncActiveAcademicYear();
      if (data) {
        setAcademicYearSyncResult(data);
        setSyncDialogOpen(true);
      } else {
        setError("Sync failed.");
      }
      return data;
    } catch (err) {
      setError("Sync failed.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const hasPlanned = academicYears.some((ay) => ay.status === AcademicYearStatus.PLANNED);
  const hasActive = academicYears.some((ay) => ay.status === AcademicYearStatus.ACTIVE);

  return {
    academicYears,
    loading,
    loaded,
    creating,
    creatingPrevious,
    error,
    hasPlanned,
    hasActive,
    actions: {
      create: create,
      createPrevious: createPrevious,
      init: initialize,
      activate: activate,
      sync: sync,
      closeSyncDialog: () => setSyncDialogOpen(false),
      close: closeAcademicYear,
      delete: deleteAcademicYear,
    },
    results: {
      syncDialogOpen,
      academicYearSyncResult,
    },
    initializationResult,
    clearInitializationResult: () => setInitializationResult(null),
  };
};
